import type { VaultV2PendingTimelock } from '../../../types/pendingTimelocks'

import { sortPendingTimelocks } from '../../../VaultV2PendingTimelock/utils'

type PendingConfigItems = VaultV2PendingTimelock[] | null | undefined

/**
 * Filters pending timelocks related to a specific adapter (VaultV2AdapterPendingData).
 * Used for VaultV1 rows in the Vaults & Markets tab.
 */
export const getAdapterPendingTimelocks = (
  pendingConfigs: PendingConfigItems,
  adapterAddress: string
): VaultV2PendingTimelock[] => {
  const filtered =
    pendingConfigs?.filter(pendingConfig => {
      if (pendingConfig.decodedData.__typename === 'VaultV2AdapterPendingData') {
        return (
          pendingConfig.decodedData.adapterAddress.toLowerCase() === adapterAddress.toLowerCase()
        )
      }
      return false
    }) ?? []

  return sortPendingTimelocks(filtered)
}

/**
 * Filters pending timelocks related to a specific market's cap (VaultV2IncreaseCapPendingData with MarketV1CapData).
 * Used for MarketV1 rows in the Vaults & Markets tab.
 *
 * A single MorphoMarketV1Adapter can serve multiple markets, so matching on the
 * adapter address alone surfaces every market's pending cap on every row. Match
 * on the market id as well so each pending action only shows on its own row.
 */
export const getMarketCapPendingTimelocks = (
  pendingConfigs: PendingConfigItems,
  adapterAddress: string,
  marketId: string
): VaultV2PendingTimelock[] => {
  const filtered =
    pendingConfigs?.filter(pendingConfig => {
      if (pendingConfig.decodedData.__typename === 'VaultV2IncreaseCapPendingData') {
        return (
          pendingConfig.decodedData.config.data?.__typename === 'MarketV1CapData' &&
          pendingConfig.decodedData.config.data.adapterAddress.toLowerCase() ===
            adapterAddress.toLowerCase() &&
          pendingConfig.decodedData.config.data.market?.marketId === marketId
        )
      }
      return false
    }) ?? []

  return sortPendingTimelocks(filtered)
}

/**
 * Filters pending timelocks related to a specific collateral's cap (VaultV2IncreaseCapPendingData with CollateralCapData).
 * Used for Collateral tab rows.
 */
export const getCollateralCapPendingTimelocks = (
  pendingConfigs: PendingConfigItems,
  collateralAddress: string
): VaultV2PendingTimelock[] => {
  const filtered =
    pendingConfigs?.filter(pendingConfig => {
      if (pendingConfig.decodedData.__typename === 'VaultV2IncreaseCapPendingData') {
        return (
          pendingConfig.decodedData.config.data?.__typename === 'CollateralCapData' &&
          pendingConfig.decodedData.config.data.collateralAddress.toLowerCase() ===
            collateralAddress.toLowerCase()
        )
      }
      return false
    }) ?? []

  return sortPendingTimelocks(filtered)
}

/**
 * Filters pending timelocks related to a specific adapter's cap (VaultV2IncreaseCapPendingData with AdapterCapData).
 * Used for Adapters tab rows.
 */
export const getAdapterCapPendingTimelocks = (
  pendingConfigs: PendingConfigItems,
  adapterAddress: string
): VaultV2PendingTimelock[] => {
  const filtered =
    pendingConfigs?.filter(pendingConfig => {
      if (pendingConfig.decodedData.__typename === 'VaultV2IncreaseCapPendingData') {
        return (
          pendingConfig.decodedData.config.data?.__typename === 'AdapterCapData' &&
          pendingConfig.decodedData.config.data.adapterAddress.toLowerCase() ===
            adapterAddress.toLowerCase()
        )
      }
      return false
    }) ?? []

  return sortPendingTimelocks(filtered)
}
